import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  FileText,
  HelpCircle,
  Layers,
  LucideIcon,
  MapPin,
  Monitor,
  Palette,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Target,
  Ticket,
  Video,
  Volume2,
  X,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { ticketService } from '@/services/ticket.service';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { languageOptions } from '@/constants/languageOptions';
import SimpleSelect from '@/components/ui/SimpleSelect';
import {
  SelfTicketItemType,
  SelfTicketItemDto,
  AvailableItemDto,
  AvailableCourseItemDto,
  AvailableCertificationItemDto,
  AvailableLearningPathItemDto,
  CourseGenerationConfig,
} from '@/types/ticket.types';

// Type configuration interface
interface TypeConfig {
  type: SelfTicketItemType;
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  selectedBg: string;
}

// Type configurations
const typeConfigs: TypeConfig[] = [
  {
    type: SelfTicketItemType.COURSE,
    label: 'Courses',
    icon: BookOpen,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-500',
    selectedBg: 'bg-indigo-100',
  },
  {
    type: SelfTicketItemType.CERTIFICATION,
    label: 'Certifications',
    icon: Award,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    selectedBg: 'bg-amber-100',
  },
  {
    type: SelfTicketItemType.LEARNING_PATH,
    label: 'Learning Paths',
    icon: MapPin,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    selectedBg: 'bg-green-100',
  },
  {
    type: SelfTicketItemType.GENERAL,
    label: 'General',
    icon: HelpCircle,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500',
    selectedBg: 'bg-purple-100',
  },
];

// Default items per page
const DEFAULT_ITEMS_PER_PAGE = 10;

// Form interface
interface TicketFormData {
  request_message: string;
  general_description: string;
  requested_suggestion_type: 'COURSE' | 'CERTIFICATION' | 'LEARNING_PATH';
}

// Skeleton component for item cards
const ItemCardSkeleton = (): JSX.Element => (
  <div className="relative p-4 rounded-xl border-2 border-gray-200">
    <div className="pr-8">
      <div className="flex items-start gap-3">
        <SkeletonLoader type="custom" className="w-12 h-12 rounded-lg" />
        <div className="flex-1 min-w-0 space-y-2">
          <SkeletonLoader type="text" className="h-4 w-3/4" />
          <SkeletonLoader type="text" className="h-3 w-full" />
          <SkeletonLoader type="text" className="h-3 w-1/2" />
        </div>
      </div>
    </div>
    <div className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 border-gray-300" />
  </div>
);

// Items grid skeleton
const ItemsGridSkeleton = (): JSX.Element => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {Array.from({ length: 6 }).map((_, index) => (
      <ItemCardSkeleton key={index} />
    ))}
  </div>
);

export default function CreateTicket(): JSX.Element {
  const navigate = useNavigate();
  // On mobile the selection summary + Submit sit below the whole item list;
  // the floating bar scrolls here.
  const submitSectionRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const [selectedType, setSelectedType] = useState<SelfTicketItemType>(SelfTicketItemType.COURSE);
  const [selectedItems, setSelectedItems] = useState<SelfTicketItemDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);
  const [expandedCertId, setExpandedCertId] = useState<string | null>(null);
  const [expandedLPId, setExpandedLPId] = useState<string | null>(null);

  // State for certification courses and learning paths (GENERAL ticket with CERTIFICATION suggestion)
  const [selectedCertificationCourses, setSelectedCertificationCourses] = useState<SelfTicketItemDto[]>([]);
  const [selectedCertificationLPs, setSelectedCertificationLPs] = useState<SelfTicketItemDto[]>([]);
  const [certCourseSearch, setCertCourseSearch] = useState('');
  const [certLPSearch, setCertLPSearch] = useState('');
  const [certCoursePage, setCertCoursePage] = useState(1);
  const [certLPPage, setCertLPPage] = useState(1);

  // Language code state (GENERAL tickets)
  const [languageCode, setLanguageCode] = useState('en-US');

  // Course generation config state (GENERAL ticket with COURSE suggestion)
  const [courseGenConfig, setCourseGenConfig] = useState<CourseGenerationConfig>({
    content_delivery_type: 'html',
    design_theme: 'educational',
    module_generation_config: { min_modules: 3, max_modules: 4 },
    content_generation_config: { min_content_items_per_module: 3, max_content_items_per_module: 4 },
  });
  const [configValidationErrors, setConfigValidationErrors] = useState<{
    moduleConfig?: string;
    contentConfig?: string;
  }>({});

  // React Hook Form
  const { register, handleSubmit, reset, watch } = useForm<TicketFormData>({
    defaultValues: {
      request_message: '',
      general_description: '',
      requested_suggestion_type: 'COURSE',
    },
  });

  const selectedSuggestionType = watch('requested_suggestion_type');
  const generalDescription = watch('general_description');
  const descriptionLength = generalDescription?.trim().length || 0;

  const currentConfig = typeConfigs.find((c) => c.type === selectedType)!;

  // Debounce search term using centralized hook
  const debouncedSearch = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);
  const debouncedCertCourseSearch = useDebounce(certCourseSearch, DEFAULT_DEBOUNCE_DELAY);
  const debouncedCertLPSearch = useDebounce(certLPSearch, DEFAULT_DEBOUNCE_DELAY);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Reset cert course page when search changes
  useEffect(() => {
    setCertCoursePage(1);
  }, [debouncedCertCourseSearch]);

  // Reset cert LP page when search changes
  useEffect(() => {
    setCertLPPage(1);
  }, [debouncedCertLPSearch]);

  // Query for available courses
  const coursesQuery = useQuery({
    queryKey: ['available-courses', debouncedSearch, currentPage, itemsPerPage],
    queryFn: () => ticketService.getAvailableCourses({
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: selectedType === SelfTicketItemType.COURSE,
  });

  // Query for available certifications
  const certificationsQuery = useQuery({
    queryKey: ['available-certifications', debouncedSearch, currentPage, itemsPerPage],
    queryFn: () => ticketService.getAvailableCertifications({
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: selectedType === SelfTicketItemType.CERTIFICATION,
  });

  // Query for available learning paths
  const learningPathsQuery = useQuery({
    queryKey: ['available-learning-paths', debouncedSearch, currentPage, itemsPerPage],
    queryFn: () => ticketService.getAvailableLearningPaths({
      search: debouncedSearch || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    enabled: selectedType === SelfTicketItemType.LEARNING_PATH,
  });

  // Query for certification courses (GENERAL ticket with CERTIFICATION suggestion) - uses assigned courses
  const certCoursesQuery = useQuery({
    queryKey: ['cert-assigned-courses', debouncedCertCourseSearch, certCoursePage],
    queryFn: () => ticketService.getAssignedCourses({
      search: debouncedCertCourseSearch || undefined,
      page: certCoursePage,
      limit: 10,
    }),
    enabled: selectedType === SelfTicketItemType.GENERAL && selectedSuggestionType === 'CERTIFICATION',
  });

  // Query for certification learning paths (GENERAL ticket with CERTIFICATION suggestion) - uses assigned learning paths
  const certLPsQuery = useQuery({
    queryKey: ['cert-assigned-lps', debouncedCertLPSearch, certLPPage],
    queryFn: () => ticketService.getAssignedLearningPaths({
      search: debouncedCertLPSearch || undefined,
      page: certLPPage,
      limit: 10,
    }),
    enabled: selectedType === SelfTicketItemType.GENERAL && selectedSuggestionType === 'CERTIFICATION',
  });

  // Get current query based on selected type
  const getCurrentQuery = () => {
    switch (selectedType) {
      case SelfTicketItemType.COURSE:
        return coursesQuery;
      case SelfTicketItemType.CERTIFICATION:
        return certificationsQuery;
      case SelfTicketItemType.LEARNING_PATH:
        return learningPathsQuery;
      case SelfTicketItemType.GENERAL:
      default:
        return null;
    }
  };

  const currentQuery = getCurrentQuery();
  const items = currentQuery?.data?.data || [];
  const pagination = currentQuery?.data?.pagination;
  const isLoading = currentQuery?.isLoading || false;
  const isError = currentQuery?.isError || false;

  // Create ticket mutation
  const createMutation = useMutation({
    mutationFn: ticketService.createRequest,
    onSuccess: () => {
      toast.success('Ticket created successfully');
      queryClient.invalidateQueries({ queryKey: ['my-ticket-requests'] });
      navigate('/learn/tickets');
    },
    onError: (error: Error & { response?: { data?: { message?: string; error?: { error_description?: string } } } }) => {
      const errorMessage = error?.response?.data?.error?.error_description
        || error?.response?.data?.message
        || 'Failed to create ticket';
      toast.error(errorMessage);
    },
  });

  // Check if item is selected
  const isItemSelected = (itemId: string): boolean => {
    return selectedItems.some((item) => item.item_id === itemId);
  };

  // Toggle item selection
  const toggleItemSelection = (item: AvailableItemDto): void => {
    if (isItemSelected(item._id)) {
      setSelectedItems(selectedItems.filter((i) => i.item_id !== item._id));
    } else {
      setSelectedItems([...selectedItems, { item_id: item._id, item_name: item.title }]);
    }
  };

  // Remove selected item
  const removeSelectedItem = (itemId: string): void => {
    setSelectedItems(selectedItems.filter((i) => i.item_id !== itemId));
  };

  // Handle type change
  const handleTypeChange = (type: SelfTicketItemType): void => {
    setSelectedType(type);
    setSelectedItems([]);
    setSearchTerm('');
    setCurrentPage(1);
    // Reset certification selections
    setSelectedCertificationCourses([]);
    setSelectedCertificationLPs([]);
    setCertCourseSearch('');
    setCertLPSearch('');
    setCertCoursePage(1);
    setCertLPPage(1);
    reset({ request_message: '', general_description: '', requested_suggestion_type: 'COURSE' });
  };

  // Handle page change
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (perPage: number): void => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  // Toggle certification course selection
  const toggleCertCourseSelection = (item: AvailableItemDto): void => {
    const isSelected = selectedCertificationCourses.some((i) => i.item_id === item._id);
    if (isSelected) {
      setSelectedCertificationCourses(selectedCertificationCourses.filter((i) => i.item_id !== item._id));
    } else {
      setSelectedCertificationCourses([...selectedCertificationCourses, {
        item_id: item._id,
        item_name: item.title,
        item_type: SelfTicketItemType.COURSE
      }]);
    }
  };

  // Toggle certification LP selection
  const toggleCertLPSelection = (item: AvailableItemDto): void => {
    const isSelected = selectedCertificationLPs.some((i) => i.item_id === item._id);
    if (isSelected) {
      setSelectedCertificationLPs(selectedCertificationLPs.filter((i) => i.item_id !== item._id));
    } else {
      setSelectedCertificationLPs([...selectedCertificationLPs, {
        item_id: item._id,
        item_name: item.title,
        item_type: SelfTicketItemType.LEARNING_PATH
      }]);
    }
  };

  // Check if certification course is selected
  const isCertCourseSelected = (itemId: string): boolean => {
    return selectedCertificationCourses.some((item) => item.item_id === itemId);
  };

  // Check if certification LP is selected
  const isCertLPSelected = (itemId: string): boolean => {
    return selectedCertificationLPs.some((item) => item.item_id === itemId);
  };

  // Submit ticket
  const onSubmit = (data: TicketFormData): void => {
    const isGeneral = selectedType === SelfTicketItemType.GENERAL;
    const isCertificationSuggestion = isGeneral && data.requested_suggestion_type === 'CERTIFICATION';

    if (!isGeneral && selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    if (isGeneral && !data.general_description?.trim()) {
      toast.error('Please provide a description for your request');
      return;
    }

    if (isGeneral && data.general_description.trim().length < 100) {
      toast.error('Description must be at least 100 characters');
      return;
    }

    if (isGeneral && data.general_description.trim().length > 1000) {
      toast.error('Description cannot exceed 1000 characters');
      return;
    }

    // Validation for GENERAL + CERTIFICATION: at least one course or learning path required
    if (isCertificationSuggestion && selectedCertificationCourses.length === 0 && selectedCertificationLPs.length === 0) {
      toast.error('Please select at least one course or learning path for certification');
      return;
    }

    const isCourseSuggestion = isGeneral && data.requested_suggestion_type === 'COURSE';

    // Validate course generation config (same as AddCourse)
    if (isCourseSuggestion) {
      let hasConfigError = false;
      if (courseGenConfig.module_generation_config.min_modules > courseGenConfig.module_generation_config.max_modules) {
        setConfigValidationErrors(prev => ({ ...prev, moduleConfig: 'Min modules cannot be greater than max modules' }));
        hasConfigError = true;
      }
      if (courseGenConfig.content_generation_config.min_content_items_per_module > courseGenConfig.content_generation_config.max_content_items_per_module) {
        setConfigValidationErrors(prev => ({ ...prev, contentConfig: 'Min content items cannot be greater than max content items' }));
        hasConfigError = true;
      }
      if (hasConfigError) {
        toast.error('Please fix the configuration errors before submitting');
        return;
      }
    }

    createMutation.mutate({
      item_type: selectedType,
      items: isGeneral ? [] : selectedItems,
      request_message: data.request_message.trim() || undefined,
      general_description: isGeneral ? data.general_description.trim() : undefined,
      requested_suggestion_type: isGeneral ? data.requested_suggestion_type : undefined,
      certification_courses: isCertificationSuggestion ? selectedCertificationCourses : undefined,
      certification_learning_paths: isCertificationSuggestion ? selectedCertificationLPs : undefined,
      language_code: isGeneral ? languageCode : undefined,
      course_generation_config: isCourseSuggestion ? courseGenConfig : undefined,
    });
  };

  const TypeIcon = currentConfig.icon;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex items-start sm:items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/learn/tickets')}
            className="p-1.5 sm:p-2 -ml-1 sm:ml-0 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-1">
              <Ticket className="w-6 h-6 sm:w-7 sm:h-7" />
              <h1 className="text-xl sm:text-2xl font-bold">Create Ticket</h1>
            </div>
            <p className="text-purple-100 text-sm sm:text-base">
              Select the items you want assigned to you and submit for approval
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Content - Left Side */}
        <div className="lg:col-span-2 space-y-4">
          {/* Type Tabs */}
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="flex border-b">
              {typeConfigs.map((config) => {
                const Icon = config.icon;
                const isActive = selectedType === config.type;
                return (
                  <button
                    key={config.type}
                    onClick={() => handleTypeChange(config.type)}
                    className={`flex-1 min-w-0 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 sm:px-4 py-2.5 sm:py-3 text-[11px] sm:text-sm leading-tight text-center font-medium transition-colors border-b-2 -mb-px ${
                      isActive
                        ? `${config.color} ${config.borderColor}`
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </button>
                );
              })}
            </div>

            {/* For GENERAL type - Show description form, for others - Show search and items */}
            {selectedType === SelfTicketItemType.GENERAL ? (
              <div className="p-4 sm:p-6 space-y-5">
                {/* Suggestion Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    What type of suggestion are you looking for?
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    {[
                      { value: 'COURSE', label: 'Course', icon: BookOpen, color: 'indigo' },
                      { value: 'CERTIFICATION', label: 'Certification', icon: Award, color: 'amber' },
                      { value: 'LEARNING_PATH', label: 'Learning Path', icon: MapPin, color: 'green' },
                    ].map((option) => {
                      const Icon = option.icon;
                      const isSelected = selectedSuggestionType === option.value;
                      return (
                        <label
                          key={option.value}
                          className={`relative flex flex-col items-center text-center p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected
                              ? `border-${option.color}-500 bg-${option.color}-50`
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            {...register('requested_suggestion_type')}
                            value={option.value}
                            className="sr-only"
                          />
                          <Icon className={`w-6 h-6 mb-2 ${isSelected ? `text-${option.color}-600` : 'text-gray-400'}`} />
                          <span className={`text-xs sm:text-sm font-medium leading-tight ${isSelected ? `text-${option.color}-700` : 'text-gray-600'}`}>
                            {option.label}
                          </span>
                          {isSelected && (
                            <div className={`absolute top-2 right-2 w-4 h-4 rounded-full bg-${option.color}-500 flex items-center justify-center`}>
                              <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Certification Courses & Learning Paths Selection - Only show when CERTIFICATION is selected */}
                {selectedSuggestionType === 'CERTIFICATION' && (
                  <div className="space-y-5">
                    {/* Two Column Layout for larger screens */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Courses Selection Card */}
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-5 h-5 text-white" />
                              <h4 className="font-semibold text-white">Courses</h4>
                            </div>
                            {selectedCertificationCourses.length > 0 && (
                              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium text-white">
                                {selectedCertificationCourses.length} selected
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Search */}
                        <div className="p-3 border-b bg-gray-50">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search courses..."
                              value={certCourseSearch}
                              onChange={(e) => setCertCourseSearch(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        {/* Course List */}
                        <div className="max-h-[280px] overflow-y-auto">
                          {certCoursesQuery.isLoading ? (
                            <div className="flex flex-col items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                              <p className="text-sm text-gray-500 mt-2">Loading courses...</p>
                            </div>
                          ) : certCoursesQuery.data?.data && certCoursesQuery.data.data.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                              {certCoursesQuery.data.data.map((course) => {
                                const isSelected = isCertCourseSelected(course._id);
                                return (
                                  <div
                                    key={course._id}
                                    onClick={() => toggleCertCourseSelection(course)}
                                    className={`flex items-start gap-3 p-3 cursor-pointer transition-all hover:bg-gray-50 ${
                                      isSelected ? 'bg-indigo-50/50' : ''
                                    }`}
                                  >
                                    {/* Thumbnail or Icon */}
                                    {course.thumbnail_image_url ? (
                                      <img
                                        src={course.thumbnail_image_url}
                                        alt={course.title}
                                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <BookOpen className="w-6 h-6 text-indigo-500" />
                                      </div>
                                    )}

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 line-clamp-1">{course.title}</p>
                                      {course.description && (
                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{course.description}</p>
                                      )}
                                    </div>

                                    {/* Checkbox */}
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                      isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                              <BookOpen className="w-10 h-10 opacity-30 mb-2" />
                              <p className="text-sm font-medium">No courses found</p>
                              <p className="text-xs">Try a different search term</p>
                            </div>
                          )}
                        </div>

                        {/* Course Pagination */}
                        {certCoursesQuery.data?.pagination && certCoursesQuery.data.pagination.total_pages > 1 && (
                          <Pagination
                            currentPage={certCoursePage}
                            totalPages={certCoursesQuery.data.pagination.total_pages}
                            totalItems={certCoursesQuery.data.pagination.total}
                            itemsPerPage={10}
                            onPageChange={setCertCoursePage}
                            showItemsPerPage={false}
                            compact={true}
                          />
                        )}
                      </div>

                      {/* Learning Paths Selection Card */}
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-white" />
                              <h4 className="font-semibold text-white">Learning Paths</h4>
                            </div>
                            {selectedCertificationLPs.length > 0 && (
                              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium text-white">
                                {selectedCertificationLPs.length} selected
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Search */}
                        <div className="p-3 border-b bg-gray-50">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search learning paths..."
                              value={certLPSearch}
                              onChange={(e) => setCertLPSearch(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        {/* LP List */}
                        <div className="max-h-[280px] overflow-y-auto">
                          {certLPsQuery.isLoading ? (
                            <div className="flex flex-col items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                              <p className="text-sm text-gray-500 mt-2">Loading learning paths...</p>
                            </div>
                          ) : certLPsQuery.data?.data && certLPsQuery.data.data.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                              {certLPsQuery.data.data.map((lp) => {
                                const isSelected = isCertLPSelected(lp._id);
                                return (
                                  <div
                                    key={lp._id}
                                    onClick={() => toggleCertLPSelection(lp)}
                                    className={`flex items-start gap-3 p-3 cursor-pointer transition-all hover:bg-gray-50 ${
                                      isSelected ? 'bg-green-50/50' : ''
                                    }`}
                                  >
                                    {/* Thumbnail or Icon */}
                                    {lp.thumbnail_image_url ? (
                                      <img
                                        src={lp.thumbnail_image_url}
                                        alt={lp.title}
                                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-6 h-6 text-green-500" />
                                      </div>
                                    )}

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 line-clamp-1">{lp.title}</p>
                                      {lp.description && (
                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{lp.description}</p>
                                      )}
                                    </div>

                                    {/* Checkbox */}
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                      isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                              <MapPin className="w-10 h-10 opacity-30 mb-2" />
                              <p className="text-sm font-medium">No learning paths found</p>
                              <p className="text-xs">Try a different search term</p>
                            </div>
                          )}
                        </div>

                        {/* LP Pagination */}
                        {certLPsQuery.data?.pagination && certLPsQuery.data.pagination.total_pages > 1 && (
                          <Pagination
                            currentPage={certLPPage}
                            totalPages={certLPsQuery.data.pagination.total_pages}
                            totalItems={certLPsQuery.data.pagination.total}
                            itemsPerPage={10}
                            onPageChange={setCertLPPage}
                            showItemsPerPage={false}
                            compact={true}
                          />
                        )}
                      </div>
                    </div>

                  </div>
                )}

                {/* Selected Items Card - Shows above description when items are selected */}
                {selectedSuggestionType === 'CERTIFICATION' && (selectedCertificationCourses.length > 0 || selectedCertificationLPs.length > 0) && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-amber-600" />
                        <h4 className="font-semibold text-gray-900">Selected Items</h4>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          {selectedCertificationCourses.length + selectedCertificationLPs.length} total
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCertificationCourses([]);
                          setSelectedCertificationLPs([]);
                        }}
                        className="text-xs text-gray-500 hover:text-red-600 transition-colors font-medium"
                      >
                        Clear all
                      </button>
                    </div>

                    <div className="p-4 space-y-4">
                      {/* Selected Courses */}
                      {selectedCertificationCourses.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="w-4 h-4 text-indigo-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Courses ({selectedCertificationCourses.length})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedCertificationCourses.map((course) => (
                              <div
                                key={course.item_id}
                                className="group flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg"
                              >
                                <BookOpen className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                                <span className="text-sm text-indigo-900 font-medium max-w-[200px] truncate">
                                  {course.item_name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedCertificationCourses(
                                    selectedCertificationCourses.filter(c => c.item_id !== course.item_id)
                                  )}
                                  className="p-0.5 rounded hover:bg-indigo-200 text-indigo-400 hover:text-indigo-600 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected Learning Paths */}
                      {selectedCertificationLPs.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Learning Paths ({selectedCertificationLPs.length})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedCertificationLPs.map((lp) => (
                              <div
                                key={lp.item_id}
                                className="group flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg"
                              >
                                <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm text-green-900 font-medium max-w-[200px] truncate">
                                  {lp.item_name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedCertificationLPs(
                                    selectedCertificationLPs.filter(l => l.item_id !== lp.item_id)
                                  )}
                                  className="p-0.5 rounded hover:bg-green-200 text-green-400 hover:text-green-600 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Description
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    {...register('general_description')}
                    maxLength={1000}
                    placeholder="Describe what kind of course, certification, or learning path you're looking for. Be specific about topics, skills, or goals..."
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                  />
                  <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-1 mt-2">
                    <p className="text-xs text-gray-500">
                      Tip: Include details like your learning goals, current skill level, preferred topics, or any specific areas you want to improve.
                    </p>
                    <span className={`text-xs flex-shrink-0 self-end sm:self-auto sm:ml-2 ${
                      descriptionLength >= 1000
                        ? 'text-red-500 font-medium'
                        : descriptionLength > 0 && descriptionLength < 100
                        ? 'text-amber-500 font-medium'
                        : 'text-gray-400'
                    }`}>
                      {generalDescription?.length ?? 0}/1000 (min 100)
                    </span>
                  </div>
                </div>

                {/* Language Selection */}
                <SimpleSelect
                  value={languageCode}
                  onChange={(val) => setLanguageCode(val as string || 'en-US')}
                  options={languageOptions}
                  label="Language"
                  placeholder="Select language..."
                />

                {/* Course Generation Config - Only when COURSE suggestion type */}
                {selectedSuggestionType === 'COURSE' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                      <SlidersHorizontal className="w-4 h-4 text-indigo-600" />
                      <h3 className="text-sm font-semibold text-gray-800">Content & Module Configuration</h3>
                    </div>

                    {/* Content Delivery Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content Delivery Type</label>
                      <div className="grid grid-cols-3 gap-2">
                        {([
                          { value: 'html', label: 'HTML Slides', icon: Monitor, desc: 'Interactive slides' },
                          { value: 'html_with_audio', label: 'HTML + Audio', icon: Volume2, desc: 'Slides with narration' },
                          { value: 'video_presentation', label: 'Video', icon: Video, desc: 'Video presentation' },
                        ] as const).map((opt) => {
                          const Icon = opt.icon;
                          const isActive = courseGenConfig.content_delivery_type === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setCourseGenConfig(prev => ({ ...prev, content_delivery_type: opt.value }))}
                              className={`flex flex-col items-center p-2 sm:p-3 rounded-xl border-2 transition-all text-center ${
                                isActive
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <Icon className={`w-5 h-5 mb-1.5 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                              <span className={`text-xs font-medium ${isActive ? 'text-indigo-700' : 'text-gray-600'}`}>{opt.label}</span>
                              <span className="text-[10px] text-gray-400 mt-0.5">{opt.desc}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Design Theme */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Design Theme</label>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {([
                          { value: 'educational', label: 'Educational', color: 'amber' },
                          { value: 'modern', label: 'Modern', color: 'blue' },
                          { value: 'corporate', label: 'Corporate', color: 'gray' },
                          { value: 'dark', label: 'Dark', color: 'slate' },
                          { value: 'minimal', label: 'Minimal', color: 'emerald' },
                        ] as const).map((theme) => {
                          const isActive = courseGenConfig.design_theme === theme.value;
                          return (
                            <button
                              key={theme.value}
                              type="button"
                              onClick={() => setCourseGenConfig(prev => ({ ...prev, design_theme: theme.value }))}
                              className={`p-2 rounded-lg border-2 transition-all text-center ${
                                isActive
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <Palette className={`w-4 h-4 mx-auto mb-1 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                              <span className={`text-[11px] font-medium ${isActive ? 'text-indigo-700' : 'text-gray-600'}`}>{theme.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Module Generation Config */}
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Min Modules: {courseGenConfig.module_generation_config.min_modules}
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={courseGenConfig.module_generation_config.min_modules}
                            onChange={(e) => {
                              const newMin = parseInt(e.target.value);
                              setCourseGenConfig(prev => ({
                                ...prev,
                                module_generation_config: { ...prev.module_generation_config, min_modules: newMin },
                              }));
                              setConfigValidationErrors(prev => ({
                                ...prev,
                                moduleConfig: newMin > courseGenConfig.module_generation_config.max_modules
                                  ? 'Min modules cannot be greater than max modules' : undefined,
                              }));
                            }}
                            className="w-full"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Max Modules: {courseGenConfig.module_generation_config.max_modules}
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={courseGenConfig.module_generation_config.max_modules}
                            onChange={(e) => {
                              const newMax = parseInt(e.target.value);
                              setCourseGenConfig(prev => ({
                                ...prev,
                                module_generation_config: { ...prev.module_generation_config, max_modules: newMax },
                              }));
                              setConfigValidationErrors(prev => ({
                                ...prev,
                                moduleConfig: courseGenConfig.module_generation_config.min_modules > newMax
                                  ? 'Min modules cannot be greater than max modules' : undefined,
                              }));
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {configValidationErrors.moduleConfig && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {configValidationErrors.moduleConfig}
                        </p>
                      )}
                    </div>

                    {/* Content Generation Config */}
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Min Content Items per Module: {courseGenConfig.content_generation_config.min_content_items_per_module}
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="6"
                            value={courseGenConfig.content_generation_config.min_content_items_per_module}
                            onChange={(e) => {
                              const newMin = parseInt(e.target.value);
                              setCourseGenConfig(prev => ({
                                ...prev,
                                content_generation_config: { ...prev.content_generation_config, min_content_items_per_module: newMin },
                              }));
                              setConfigValidationErrors(prev => ({
                                ...prev,
                                contentConfig: newMin > courseGenConfig.content_generation_config.max_content_items_per_module
                                  ? 'Min content items cannot be greater than max content items' : undefined,
                              }));
                            }}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500">Range: 1-6 (Default: 3)</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Max Content Items per Module: {courseGenConfig.content_generation_config.max_content_items_per_module}
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="6"
                            value={courseGenConfig.content_generation_config.max_content_items_per_module}
                            onChange={(e) => {
                              const newMax = parseInt(e.target.value);
                              setCourseGenConfig(prev => ({
                                ...prev,
                                content_generation_config: { ...prev.content_generation_config, max_content_items_per_module: newMax },
                              }));
                              setConfigValidationErrors(prev => ({
                                ...prev,
                                contentConfig: courseGenConfig.content_generation_config.min_content_items_per_module > newMax
                                  ? 'Min content items cannot be greater than max content items' : undefined,
                              }));
                            }}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500">Range: 1-6 (Default: 4)</p>
                        </div>
                      </div>
                      {configValidationErrors.contentConfig && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {configValidationErrors.contentConfig}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="p-4 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${currentConfig.label.toLowerCase()}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Items Grid */}
                <div className="p-3 sm:p-4 sm:max-h-[500px] sm:overflow-y-auto">
                  {isLoading ? (
                    <ItemsGridSkeleton />
                  ) : isError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-red-500">
                      <X className="w-10 h-10 mb-3 opacity-40" />
                      <p className="font-medium">Failed to load items</p>
                      <p className="text-sm">Please try again</p>
                    </div>
                  ) : items.length > 0 ? (
                    <div className={`grid gap-3 ${selectedType === SelfTicketItemType.GENERAL ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      {items.map((item) => {
                        const isSelected = isItemSelected(item._id);

                        // Enhanced course card
                        if (selectedType === SelfTicketItemType.COURSE) {
                          const course = item as AvailableCourseItemDto;
                          const isExpanded = expandedCourseId === course._id;
                          const totalContentItems = course.modules?.reduce((sum, m) => sum + m.content_item_count, 0) || 0;
                          const totalAssessments = course.assessment_summary
                            ? (course.assessment_summary.course_pre ? 1 : 0)
                              + (course.assessment_summary.course_post ? 1 : 0)
                              + course.assessment_summary.module_pre_count
                              + course.assessment_summary.module_post_count
                              + course.assessment_summary.content_pre_count
                              + course.assessment_summary.content_post_count
                            : 0;

                          return (
                            <div
                              key={course._id}
                              className={`relative rounded-xl border-2 transition-all duration-200 ${
                                isSelected
                                  ? `${currentConfig.selectedBg} ${currentConfig.borderColor}`
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                              }`}
                            >
                              {/* Main clickable area */}
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => toggleItemSelection(item)}
                              >
                                {/* Selection Indicator */}
                                <div
                                  className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? `${currentConfig.bgColor} ${currentConfig.borderColor}`
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {isSelected && <Check className={`w-3 h-3 ${currentConfig.color}`} />}
                                </div>

                                <div className="pr-8">
                                  <div className="flex items-start gap-3">
                                    {course.thumbnail_image_url ? (
                                      <img
                                        src={course.thumbnail_image_url}
                                        alt={course.title}
                                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className={`p-2 rounded-lg ${currentConfig.bgColor} flex-shrink-0`}>
                                        <TypeIcon className={`w-5 h-5 ${currentConfig.color}`} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                        {course.title}
                                      </h4>
                                      {course.description && (
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-3 sm:line-clamp-none">
                                          {course.description}
                                        </p>
                                      )}
                                      {/* Quick stats row */}
                                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                                        {course.difficulty && (
                                          <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                            {course.difficulty}
                                          </span>
                                        )}
                                        {course.estimated_duration && (course.estimated_duration.hours > 0 || course.estimated_duration.minutes > 0) && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                            <Clock className="w-3 h-3" />
                                            {course.estimated_duration.hours > 0 && `${course.estimated_duration.hours}h `}
                                            {course.estimated_duration.minutes > 0 && `${course.estimated_duration.minutes}m`}
                                          </span>
                                        )}
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <Layers className="w-3 h-3" />
                                          {course.module_count ?? 0} modules
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <FileText className="w-3 h-3" />
                                          {totalContentItems} contents
                                        </span>
                                        {totalAssessments > 0 && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                            <ClipboardCheck className="w-3 h-3" />
                                            {totalAssessments} assessments
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Expand/Collapse toggle */}
                              {course.modules && course.modules.length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCourseId(isExpanded ? null : course._id);
                                  }}
                                  className="w-full flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 border-t border-gray-100 transition-colors rounded-b-xl"
                                >
                                  {isExpanded ? (
                                    <>Hide details <ChevronUp className="w-3.5 h-3.5" /></>
                                  ) : (
                                    <>View details <ChevronDown className="w-3.5 h-3.5" /></>
                                  )}
                                </button>
                              )}

                              {/* Expanded details */}
                              {isExpanded && course.modules && (
                                <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                                  {/* Assessment Summary */}
                                  {course.assessment_summary && totalAssessments > 0 && (
                                    <div>
                                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Assessments</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {course.assessment_summary.course_pre && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">Course Pre-Assessment</span>
                                        )}
                                        {course.assessment_summary.course_post && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">Course Post-Assessment</span>
                                        )}
                                        {course.assessment_summary.module_pre_count > 0 && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{course.assessment_summary.module_pre_count} Module Pre</span>
                                        )}
                                        {course.assessment_summary.module_post_count > 0 && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{course.assessment_summary.module_post_count} Module Post</span>
                                        )}
                                        {course.assessment_summary.content_pre_count > 0 && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{course.assessment_summary.content_pre_count} Content Pre</span>
                                        )}
                                        {course.assessment_summary.content_post_count > 0 && (
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{course.assessment_summary.content_post_count} Content Post</span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Modules */}
                                  <div>
                                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                      Modules ({course.modules.length})
                                    </p>
                                    <div className="space-y-2">
                                      {course.modules.map((mod, idx) => (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                                          <div className="flex items-start gap-2">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold mt-0.5">
                                              {idx + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-medium text-gray-900">{mod.title}</p>
                                              {mod.description && (
                                                <p className="text-[10px] text-gray-500 mt-0.5">{mod.description}</p>
                                              )}
                                              {/* Content items */}
                                              {mod.content_items && mod.content_items.length > 0 && (
                                                <div className="mt-1.5 flex flex-wrap gap-1">
                                                  {mod.content_items.map((ci, ciIdx) => (
                                                    <span
                                                      key={ciIdx}
                                                      className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-600"
                                                    >
                                                      <FileText className="w-2.5 h-2.5 text-gray-400" />
                                                      {ci.title}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                              {/* Module stats */}
                                              <p className="text-[10px] text-gray-400 mt-1">
                                                {mod.content_item_count} content{mod.content_item_count !== 1 ? 's' : ''}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Enhanced certification card
                        if (selectedType === SelfTicketItemType.CERTIFICATION) {
                          const cert = item as AvailableCertificationItemDto;
                          const isExpandedCert = expandedCertId === cert._id;
                          const hasDetails = cert.required_courses?.length > 0 || cert.learning_paths?.length > 0;

                          return (
                            <div
                              key={cert._id}
                              className={`relative rounded-xl border-2 transition-all duration-200 ${
                                isSelected
                                  ? `${currentConfig.selectedBg} ${currentConfig.borderColor}`
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                              }`}
                            >
                              {/* Main clickable area */}
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => toggleItemSelection(item)}
                              >
                                {/* Selection Indicator */}
                                <div
                                  className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? `${currentConfig.bgColor} ${currentConfig.borderColor}`
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {isSelected && <Check className={`w-3 h-3 ${currentConfig.color}`} />}
                                </div>

                                <div className="pr-8">
                                  <div className="flex items-start gap-3">
                                    {cert.thumbnail_image_url ? (
                                      <img
                                        src={cert.thumbnail_image_url}
                                        alt={cert.title}
                                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className={`p-2 rounded-lg ${currentConfig.bgColor} flex-shrink-0`}>
                                        <TypeIcon className={`w-5 h-5 ${currentConfig.color}`} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                        {cert.title}
                                      </h4>
                                      {cert.description && (
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-3 sm:line-clamp-none">
                                          {cert.description}
                                        </p>
                                      )}
                                      {/* Quick stats row */}
                                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <ClipboardCheck className="w-3 h-3" />
                                          {cert.total_questions} questions
                                        </span>
                                        {cert.time_limit_minutes && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                            <Clock className="w-3 h-3" />
                                            {cert.time_limit_minutes} min
                                          </span>
                                        )}
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <Target className="w-3 h-3" />
                                          {cert.passing_score}% to pass
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <RotateCcw className="w-3 h-3" />
                                          {cert.max_attempts} attempts
                                        </span>
                                        {cert.required_courses?.length > 0 && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                            <BookOpen className="w-3 h-3" />
                                            {cert.required_courses.length} course{cert.required_courses.length !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {cert.learning_paths?.length > 0 && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                            <MapPin className="w-3 h-3" />
                                            {cert.learning_paths.length} learning path{cert.learning_paths.length !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Expand/Collapse toggle */}
                              {hasDetails && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCertId(isExpandedCert ? null : cert._id);
                                  }}
                                  className="w-full flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium text-amber-600 hover:bg-amber-50 border-t border-gray-100 transition-colors rounded-b-xl"
                                >
                                  {isExpandedCert ? (
                                    <>Hide details <ChevronUp className="w-3.5 h-3.5" /></>
                                  ) : (
                                    <>View details <ChevronDown className="w-3.5 h-3.5" /></>
                                  )}
                                </button>
                              )}

                              {/* Expanded details */}
                              {isExpandedCert && (
                                <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                                  {/* Required Courses */}
                                  {cert.required_courses?.length > 0 && (
                                    <div>
                                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Required Courses ({cert.required_courses.length})
                                      </p>
                                      <div className="space-y-1.5">
                                        {cert.required_courses.map((course, idx) => (
                                          <div key={course._id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                                              {idx + 1}
                                            </span>
                                            <BookOpen className="w-3 h-3 text-indigo-500 flex-shrink-0" />
                                            <p className="text-xs text-gray-900 truncate">{course.title}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Learning Paths */}
                                  {cert.learning_paths?.length > 0 && (
                                    <div>
                                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Learning Paths ({cert.learning_paths.length})
                                      </p>
                                      <div className="space-y-1.5">
                                        {cert.learning_paths.map((lp, idx) => (
                                          <div key={lp._id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-[10px] font-bold">
                                              {idx + 1}
                                            </span>
                                            <MapPin className="w-3 h-3 text-green-500 flex-shrink-0" />
                                            <p className="text-xs text-gray-900 truncate">{lp.title}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Enhanced learning path card
                        if (selectedType === SelfTicketItemType.LEARNING_PATH) {
                          const lp = item as AvailableLearningPathItemDto;
                          const isExpandedLP = expandedLPId === lp._id;
                          const hasCourses = lp.courses?.length > 0;

                          return (
                            <div
                              key={lp._id}
                              className={`relative rounded-xl border-2 transition-all duration-200 ${
                                isSelected
                                  ? `${currentConfig.selectedBg} ${currentConfig.borderColor}`
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                              }`}
                            >
                              {/* Main clickable area */}
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => toggleItemSelection(item)}
                              >
                                {/* Selection Indicator */}
                                <div
                                  className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? `${currentConfig.bgColor} ${currentConfig.borderColor}`
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {isSelected && <Check className={`w-3 h-3 ${currentConfig.color}`} />}
                                </div>

                                <div className="pr-8">
                                  <div className="flex items-start gap-3">
                                    {lp.thumbnail_image_url ? (
                                      <img
                                        src={lp.thumbnail_image_url}
                                        alt={lp.title}
                                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className={`p-2 rounded-lg ${currentConfig.bgColor} flex-shrink-0`}>
                                        <TypeIcon className={`w-5 h-5 ${currentConfig.color}`} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                        {lp.title}
                                      </h4>
                                      {lp.description && (
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-3 sm:line-clamp-none">
                                          {lp.description}
                                        </p>
                                      )}
                                      {/* Quick stats row */}
                                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                          <BookOpen className="w-3 h-3" />
                                          {lp.total_courses} course{lp.total_courses !== 1 ? 's' : ''}
                                        </span>
                                        {lp.difficulty_progression && (
                                          <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                            {lp.difficulty_progression}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Expand/Collapse toggle */}
                              {hasCourses && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedLPId(isExpandedLP ? null : lp._id);
                                  }}
                                  className="w-full flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium text-green-600 hover:bg-green-50 border-t border-gray-100 transition-colors rounded-b-xl"
                                >
                                  {isExpandedLP ? (
                                    <>Hide details <ChevronUp className="w-3.5 h-3.5" /></>
                                  ) : (
                                    <>View details <ChevronDown className="w-3.5 h-3.5" /></>
                                  )}
                                </button>
                              )}

                              {/* Expanded details */}
                              {isExpandedLP && lp.courses && (
                                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Courses ({lp.courses.length})
                                  </p>
                                  <div className="space-y-2">
                                    {lp.courses.map((course, idx) => (
                                      <div key={course.course_id} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                                        <div className="flex items-start gap-2">
                                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-[10px] font-bold mt-0.5">
                                            {idx + 1}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <p className="text-xs font-medium text-gray-900">{course.course_title}</p>
                                              {course.difficulty_level && (
                                                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                                                  {course.difficulty_level}
                                                </span>
                                              )}
                                            </div>
                                            {course.course_description && (
                                              <p className="text-[10px] text-gray-500 mt-0.5">{course.course_description}</p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Default card (fallback)
                        return (
                          <div
                            key={item._id}
                            onClick={() => toggleItemSelection(item)}
                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? `${currentConfig.selectedBg} ${currentConfig.borderColor}`
                                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                            }`}
                          >
                            {/* Selection Indicator */}
                            <div
                              className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? `${currentConfig.bgColor} ${currentConfig.borderColor}`
                                  : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <Check className={`w-3 h-3 ${currentConfig.color}`} />}
                            </div>

                            {/* Content */}
                            <div className="pr-8">
                              <div className="flex items-start gap-3">
                                {item.thumbnail_image_url ? (
                                  <img
                                    src={item.thumbnail_image_url}
                                    alt={item.title}
                                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className={`p-2 rounded-lg ${currentConfig.bgColor} flex-shrink-0`}>
                                    <TypeIcon className={`w-5 h-5 ${currentConfig.color}`} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                    {item.title}
                                  </h4>
                                  {item.description && (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Search className="w-10 h-10 mb-3 opacity-40" />
                      <p className="font-medium">No items found</p>
                      <p className="text-sm">Try adjusting your search</p>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {pagination && pagination.total_pages > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.total_pages}
                    totalItems={pagination.total}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Sidebar - Right Side */}
        <div className="lg:col-span-1" ref={submitSectionRef}>
          <div className="bg-white rounded-xl shadow-sm border lg:sticky lg:top-4">
            {/* Selected Items Header - Hide for GENERAL type */}
            {selectedType !== SelfTicketItemType.GENERAL && (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Selected Items</h3>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${currentConfig.bgColor} ${currentConfig.color}`}>
                      {selectedItems.length} selected
                    </span>
                  </div>
                </div>

                {/* Selected Items List */}
                <div className="p-4 max-h-[200px] overflow-y-auto">
                  {selectedItems.length > 0 ? (
                    <div className="space-y-2">
                      {selectedItems.map((item) => (
                        <div
                          key={item.item_id}
                          className={`flex items-center justify-between p-2.5 rounded-lg ${currentConfig.bgColor}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <TypeIcon className={`w-4 h-4 flex-shrink-0 ${currentConfig.color}`} />
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {item.item_name}
                            </span>
                          </div>
                          <button
                            onClick={() => removeSelectedItem(item.item_id)}
                            className="p-1 hover:bg-white rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <TypeIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No items selected</p>
                      <p className="text-xs mt-1">Click on items to select them</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Form - wraps all input fields and submit button */}
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* For GENERAL type - Show message field at top, then info box */}
              {selectedType === SelfTicketItemType.GENERAL && (
                <>
                  {/* Message to Approvers for GENERAL tickets - at top */}
                  <div className="p-4 border-b">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message to Approvers
                      <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                    </label>
                    <textarea
                      {...register('request_message')}
                      placeholder="Add any additional message for the approvers..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                    />
                  </div>
                  {/* Info box */}
                  <div className="p-4">
                    <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <HelpCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-purple-800 mb-2">General Request</h4>
                        <p className="text-sm text-purple-700 leading-relaxed">
                          Use this option when you need assistance but aren&apos;t sure which specific course, certification, or learning path to request.
                        </p>
                        <p className="text-sm text-purple-700 mt-2 leading-relaxed">
                          Describe what you&apos;re looking for and an approver will suggest appropriate items for you.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Message Section - Only show for non-GENERAL types */}
              {selectedType !== SelfTicketItemType.GENERAL && (
                <div className="p-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message to Approvers
                    <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                  </label>
                  <textarea
                    {...register('request_message')}
                    placeholder="Explain why you need these items..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                  />
                </div>
              )}

              {/* Actions */}
              <div className={`p-4 bg-gray-50 rounded-b-xl ${selectedType !== SelfTicketItemType.GENERAL ? 'border-t' : ''}`}>
                <button
                  type="submit"
                  disabled={createMutation.isPending || (selectedType === SelfTicketItemType.GENERAL && descriptionLength < 100)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium ${
                    selectedType === SelfTicketItemType.GENERAL
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  <Ticket className="w-4 h-4" />
                  {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/learn/tickets')}
                  className="w-full mt-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Mobile/tablet only: once items are picked, a floating bar jumps to the
          selection + Submit section, which is otherwise below the full list. */}
      {selectedType !== SelfTicketItemType.GENERAL && selectedItems.length > 0 && (
        <>
          {/* Spacer so the bar never covers the last card / Cancel button */}
          <div className="h-16 lg:hidden" aria-hidden="true" />
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-4 py-3 flex items-center justify-between gap-3">
            <span className={`text-sm font-medium ${currentConfig.color}`}>
              {selectedItems.length} selected
            </span>
            <button
              type="button"
              onClick={() => submitSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700"
            >
              <Ticket className="w-4 h-4" />
              Review &amp; Submit
            </button>
          </div>
        </>
      )}
    </div>
  );
}
